import FilterPopover from 'components/FilterPopover';
import FilterButton from 'components/FilterPopover/FilterButton';
import FilterButtonCondensed from 'components/FilterPopover/FilterButtonCondensed';
import React, { useEffect, useState } from 'react';
import { Manager } from 'react-popper';
import { RFIQuery } from 'records/RFIQuery';
import queryRFIs from 'utils/queryRFIs';
import { useTranslator } from '@bluebeam/react-i18n';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ActiveItemsCheckbox from './ActiveItemsCheckbox';
import AssignedToMeCheckbox from './AssignedToMeCheckbox';
import AssigneeFilterSection from './AssigneeFilterSection';
import ClosedItemsCheckbox from './ClosedItemsCheckbox';
import OverdueItemsCheckbox from './OverdueItemsCheckbox';

interface Props extends WithAppStoreProps {
  useCondensed?: boolean;
}

const RFIFilterPopover = ({
  useCondensed = false,
  actions,
  appState: { rfiQuery, rfis, roles, markups, sheets, interactions },
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [confirmBtnText, setConfirmButtonText] = useState<string>('');
  const [currentRFIQuery, setCurrentRFIQuery] = useState<RFIQuery>(
    new RFIQuery()
  );

  const { translate } = useTranslator(['filters']);

  useEffect(() => {
    getButtonText();
  }, [currentRFIQuery]);

  useEffect(() => {
    if (rfiQuery !== currentRFIQuery) {
      setCurrentRFIQuery(rfiQuery);
    }
  }, [rfiQuery]);

  const getButtonText = (): void => {
    const updatedRFIs = queryRFIs(
      rfis,
      roles,
      markups,
      sheets,
      currentRFIQuery
    );
    const itemsCount = updatedRFIs.size;
    const totalItemsCount = rfis.size;
    let btnText = '';

    if (itemsCount === totalItemsCount) {
      btnText = translate('filters:showAllItems');
    } else {
      btnText = `${translate('filters:show')} ${itemsCount} ${translate(
        'filters:item'
      )}${itemsCount !== 1 ? 's' : ''}`;
    }

    setConfirmButtonText(btnText);
  };

  const handleReset = async (): Promise<void> => {
    const resetRFIQuery = currentRFIQuery
      .delete('assignees')
      .delete('overdue')
      .delete('open')
      .delete('closed');

    setCurrentRFIQuery(resetRFIQuery);
  };

  const handleFilterConfirm = (): void => {
    actions.setRFIQuery(currentRFIQuery);
    setOpen(false);
  };

  const getFilterApplied = (): boolean => {
    if (rfis.size === 0) {
      return false;
    }

    return currentRFIQuery.filterApplied();
  };

  const handleFilterToggle = (): void => {
    const rfiOverlayOpen = interactions.some((i) =>
      ['CreateRFI', 'ViewRFI'].includes(i.type)
    );

    if (!rfiOverlayOpen) {
      setOpen(!open);
    }
  };

  const handleUpdateLocalRFIQuery = (nextRFIQuery: RFIQuery) => {
    setCurrentRFIQuery(nextRFIQuery);
  };

  return (
    <Manager>
      {useCondensed ? (
        <FilterButtonCondensed
          onFilterClick={() => handleFilterToggle()}
          filterApplied={getFilterApplied()}
        />
      ) : (
        <FilterButton
          onFilterClick={() => handleFilterToggle()}
          filterApplied={getFilterApplied()}
        />
      )}
      <FilterPopover
        title={translate('filters:filters')}
        open={open}
        onClose={() => handleFilterToggle()}
        onReset={() => handleReset()}
        onConfirm={() => handleFilterConfirm()}
        confirmBtnText={confirmBtnText}
      >
        <ActiveItemsCheckbox
          rfiQuery={currentRFIQuery}
          updateRFIQuery={(nextRFIQuery: RFIQuery) =>
            handleUpdateLocalRFIQuery(nextRFIQuery)
          }
        />
        <ClosedItemsCheckbox
          rfiQuery={currentRFIQuery}
          updateRFIQuery={(nextRFIQuery: RFIQuery) =>
            handleUpdateLocalRFIQuery(nextRFIQuery)
          }
        />
        <OverdueItemsCheckbox
          rfiQuery={currentRFIQuery}
          updateRFIQuery={(nextRFIQuery: RFIQuery) =>
            handleUpdateLocalRFIQuery(nextRFIQuery)
          }
        />
        <AssignedToMeCheckbox
          rfiQuery={currentRFIQuery}
          updateRFIQuery={(nextRFIQuery: RFIQuery) =>
            handleUpdateLocalRFIQuery(nextRFIQuery)
          }
        />

        <AssigneeFilterSection
          rfiQuery={currentRFIQuery}
          updateRFIQuery={(nextRFIQuery: RFIQuery) =>
            handleUpdateLocalRFIQuery(nextRFIQuery)
          }
        />
      </FilterPopover>
    </Manager>
  );
};

export default withAppStore<Props>(RFIFilterPopover);
